<template>
  <component
    :is="pickable ? 'button' : 'div'"
    :type="pickable ? 'button' : undefined"
    :class="classes"
    :disabled="pickable && (inFlight || !slotData?.team)"
    :aria-pressed="isPick"
    :aria-label="ariaLabel"
    @click="onClick"
  >
    <template v-if="slotData?.team">
      <span :class="$style.left">
        <team-icon :team="slotData.team" size="sm" />
        <span :class="$style.name">{{ slotData.team.name }}</span>
      </span>
      <span :class="$style.right">
        <span v-if="score !== null" :class="$style.score">{{ score }}</span>
      </span>
    </template>

    <template v-else>
      <span :class="$style.placeholderRow">
        <ui-icon name="plus" :class="$style.placeholderIcon" />
        <span v-if="label" :class="$style.placeholderLabel">{{ label }}</span>
      </span>
    </template>
  </component>
</template>

<i18n lang="yaml">
ru:
  pickAria: 'Поставить пик на {team}'
  unpickableAria: 'Слот {label} — пика нельзя'
</i18n>

<script lang="ts" setup>
import { computed } from 'vue'
import TeamIcon from '../../common/TeamIcon.vue'
import UiIcon from '@/ui-core/components/icon/index.vue'
import { bracketSlotLabel } from '../../../utils/bracket-slot-label'
import type { BracketSlot } from '../../../types'

defineOptions({ name: 'PickemBracketMatchSlot' })

const props = withDefaults(
  defineProps<{
    slotData: BracketSlot
    side: 'left' | 'right'
    score: number | null
    isPick: boolean
    isOutcome: boolean
    isCorrect: boolean | null
    pickable: boolean
    inFlight: boolean
  }>(),
  {}
)

const emit = defineEmits<{
  pick: []
}>()

const cssModule = useCssModule()
const { t } = useI18n()

const label = computed(() => bracketSlotLabel(props.slotData))

const classes = computed(() => [
  cssModule.slot,
  cssModule[`side-${props.side}`],
  props.isPick && cssModule.pick,
  props.isOutcome && cssModule.outcome,
  props.isCorrect === true && cssModule.correct,
  props.isCorrect === false && cssModule.incorrect,
  !props.slotData.team && cssModule.empty,
  props.pickable && cssModule.pickable,
  props.inFlight && cssModule.inFlight
])

const ariaLabel = computed(() => {
  if (!props.slotData.team) return t('unpickableAria', { label: label.value })
  return t('pickAria', { team: props.slotData.team.name })
})

const onClick = () => {
  if (!props.pickable) return
  if (props.inFlight) return
  if (!props.slotData.team) return
  emit('pick')
}
</script>

<style lang="scss" module>
// По макету: одна команда — строка 220×46 с background control-default-bg,
// border 1px text-3 (#555F71), radius 4px. Два слота укладываются в
// BracketMatch без gap'а; их объединяющий вид появляется визуально за
// счёт соседства, а не общей рамки.
.slot {
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4em;
  width: 100%;
  height: 4.6em;
  padding: 0 1.2em;
  background-color: color-get(--root-rgb-control-default-bg);
  border: 1px solid color-get(--root-rgb-bg-border);
  border-radius: 0.4em;
  cursor: default;
  color: color-get(--root-rgb-text-2);
  text-align: left;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  // Слипаем верхний и нижний слот: общая граница рисуется один раз.
  & + & {
    margin-top: -1px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  &:first-child {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.left {
  display: flex;
  align-items: center;
  gap: 0.8em;
  min-width: 0;
  flex: 1 1 auto;
}

// Имя команды — 20px Headline/S по figma, цвет text-2 (#929BAE).
.name {
  @include typo-get(headline-s);
  color: color-get(--root-rgb-text-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.right {
  display: inline-flex;
  align-items: center;
  gap: 0.6em;
  flex-shrink: 0;
}

.score {
  @include typo-get(subhead-l);
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
  color: color-get(--root-rgb-text-1);
  min-width: 1.6em;
  text-align: right;
}

// Empty-слот (нет команды) — центрированный большой plus 32×32.
.empty {
  justify-content: center;
  padding: 1.2em;
}

.placeholderRow {
  display: inline-flex;
  align-items: center;
  gap: 0.8em;
  color: color-get(--root-rgb-text-3);
}

.placeholderIcon {
  width: 3.2em;
  height: 3.2em;
  color: color-get(--root-rgb-text-2);
}

.placeholderLabel {
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 12em;
  display: none; // По дизайну label прячется; вернуть, если бэк включит
}

.pickable {
  cursor: pointer;

  @include hover-focus {
    border-color: color-get(--root-rgb-text-2);
    .name {
      color: color-get(--root-rgb-text-1);
    }
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}

// Пик пользователя — accent-2 рамка, имя становится text-1.
.pick {
  border-color: color-get(--root-rgb-accent-2);

  .name {
    color: color-get(--root-rgb-text-1);
  }
}

// Финальный результат пика юзера в finished-этапе:
.correct {
  border-color: color-get(--root-rgb-system-success);
  background-color: color-get(--root-rgb-system-success, 0.06);

  .name {
    color: color-get(--root-rgb-text-1);
  }
}

.incorrect {
  border-color: color-get(--root-rgb-system-error);
  background-color: color-get(--root-rgb-system-error, 0.06);
}

.inFlight {
  opacity: 0.6;
  pointer-events: none;
}
</style>
